/**
 * Troubleshooting Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const TROUBLESHOOTING_ARTICLES_ES: HelpArticle[] = [
  {
    id: "ts-001",
    slug: "kyc-verification-issues",
    title: "Problemas de verificación KYC",
    description: "Resolviendo problemas de verificación de identidad",
    category: "troubleshooting",
    popular: true,
    order: 1,
    keywords: ["kyc", "verificación", "fallido", "rechazado", "problema"],
    content: `
# Problemas de verificación KYC

¿Tienes problemas con la verificación de identidad? Aquí te explicamos cómo resolver los problemas más comunes.

## Razones comunes de rechazo

### Problemas con documentos
| Problema | Solución |
|----------|----------|
| Foto borrosa | Vuelve a tomarla con buena iluminación |
| Reflejo en el documento | Evita luz directa |
| ID caducado | Usa un documento válido |
| Esquinas cortadas | Incluye las 4 esquinas |
| Tipo de documento incorrecto | Usa pasaporte/DNI/licencia |

### Problemas con selfie
| Problema | Solución |
|----------|----------|
| Cara no visible | Quítate las gafas, mira a la cámara |
| Mala iluminación | Usa luz natural |
| La foto no coincide con el ID | Asegúrate de que sea la misma persona |
| Ojos cerrados | Mantén los ojos abiertos |
| Cara cubierta | Quítate mascarillas/gorros |

## Reintentar paso a paso

### 1. Verifica los requisitos
- Documento válido, no caducado
- Original (no fotocopias)
- Texto claro y legible
- Documento completo visible

### 2. Prepara el entorno
- Buena iluminación (natural es mejor)
- Cámara/teléfono estable
- Lente de cámara limpio
- Fondo liso

### 3. Toma nuevas fotos
- Mantén firme, no te apresures
- Llena el encuadre con el documento
- Sin flash (causa reflejos)
- Revisa antes de enviar

## ¿Sigue fallando?

### Revisión manual
1. Ve a Ajustes > Verificación
2. Haz clic en "Solicitar revisión manual"
3. Sube los documentos
4. Añade explicación si es necesario
5. Espera 24-48 horas

### Contactar con soporte
Envía correo a support@supabank.xyz con:
- Correo de la cuenta
- Descripción del problema
- Capturas de pantalla de errores
- Fotos que estás intentando usar

## Significado de los estados de verificación

| Estado | Significado |
|--------|-------------|
| Pendiente | Siendo procesado |
| Necesita información | Documentos adicionales requeridos |
| Rechazado | Ver motivo, reintentar |
| Aprobado | Verificación completa |
    `.trim(),
  },
  {
    id: "ts-002",
    slug: "bank-account-connection-problems",
    title: "Problemas de conexión de cuenta bancaria",
    description: "Solucionando problemas con la configuración de cuenta bancaria",
    category: "troubleshooting",
    order: 2,
    keywords: ["banco", "cuenta", "conexión", "fallido", "vincular"],
    content: `
# Problemas de conexión de cuenta bancaria

¿Tienes problemas conectando tu cuenta bancaria? Aquí están las soluciones.

## Problemas comunes

### Datos incorrectos
**Síntomas**: Cuenta rechazada inmediatamente

**Solución**:
1. Verifica el número de routing (9 dígitos)
2. Verifica el número de cuenta
3. Confirma el tipo de cuenta
4. Haz coincidir el nombre exactamente

### Verificación por micro-depósitos fallida
**Síntomas**: No puedes verificar con los montos de depósito

**Solución**:
1. Espera 2-3 días hábiles para los depósitos
2. Verifica la cuenta correcta
3. Nota: los depósitos son centavos, no dólares
4. Intenta de nuevo con las cantidades exactas

### Nombre no coincide
**Síntomas**: Cuenta rechazada, el nombre no coincide

**Solución**:
- La cuenta bancaria debe estar a tu nombre
- Usa el nombre exactamente como aparece en la cuenta bancaria
- Las cuentas conjuntas pueden no funcionar
- Contacta con soporte para asistencia

## Métodos de verificación

### Método 1: Micro-depósitos
1. Enviamos dos depósitos pequeños
2. Revisa tu banco (2-3 días)
3. Introduce las cantidades en Supabank
4. Cuenta verificada

### Método 2: Verificación instantánea
1. Conecta vía Plaid (si está disponible)
2. Inicia sesión en tu banco
3. Verificación instantánea
4. Más seguro

### Método 3: Subir documento
1. Sube un estado de cuenta bancario
2. Debe mostrar:
   - Tu nombre
   - Número de cuenta
   - Fecha reciente
3. Verificado en 24 horas

## Problemas específicos por banco

### Chase
- Usa el número de cuenta completo (no acortado)
- Las cuentas personales funcionan mejor

### Bank of America
- El routing varía por estado
- Verifica en un cheque de papel el routing correcto

### Wells Fargo
- Usa routing de cuenta corriente, no de ahorro
- Verifica que la cuenta esté activa

## ¿Sigues teniendo problemas?

Contacta con soporte con:
- Nombre del banco
- Mensaje de error
- Captura de pantalla del problema
- (No envíes números de cuenta completos)
    `.trim(),
  },
  {
    id: "ts-003",
    slug: "delayed-transactions",
    title: "Transacciones retrasadas",
    description: "Qué hacer cuando las transacciones tardan demasiado",
    category: "troubleshooting",
    order: 3,
    keywords: ["retrasado", "lento", "esperando", "atascado", "pendiente"],
    content: `
# Transacciones retrasadas

¿La transacción tarda más de lo esperado? Esto es lo que debes verificar.

## Retrasos en depósitos

### Verificar estado en blockchain
1. Obtén el hash de tu transacción
2. Visita el explorador de bloques (Etherscan, etc.)
3. Verifica las confirmaciones
4. Ve si está pendiente en mempool

### Causas comunes

| Causa | Solución |
|-------|----------|
| Congestión de red | Espera a la confirmación |
| Comisión de gas baja | La transacción puede tardar horas |
| Red incorrecta | Contacta con soporte |
| Por debajo del mínimo | El depósito puede no procesarse |

### Tiempos esperados
- Ethereum: 2-5 minutos
- Polygon: 3-8 minutos
- Solana: 15-30 segundos
- Durante congestión: Mucho más largo

## Retrasos en pagos

### Verificar estado en Supabank
1. Ve a Transacciones
2. Haz clic en el pago
3. Verifica el estado detallado
4. Ve la llegada estimada

### Causas comunes

| Causa | Solución |
|-------|----------|
| Primer pago | Puede necesitar revisión |
| Monto grande | Verificación adicional |
| Procesamiento bancario | Espera al banco |
| Fin de semana/festivo | Bancos cerrados |

### Tiempos esperados
- ACH: 2-3 días hábiles
- Wire: 1-2 días hábiles
- SEPA: 1 día hábil

## Cuándo contactar con soporte

### Depósitos
- Confirmado en blockchain pero no aparece (>1 hora)
- Estado atascado en "Convirtiendo" (>30 minutos)
- Monto incorrecto

### Pagos
- "Enviado" por >3 días hábiles (ACH)
- "En tránsito" por >5 días hábiles
- Devuelto sin explicación

## Contactar con soporte

Incluye:
- ID de transacción
- Tiempo esperado vs real
- Hash de blockchain (depósitos)
- Capturas de pantalla
    `.trim(),
  },
  {
    id: "ts-004",
    slug: "error-codes-explained",
    title: "Explicación de códigos de error",
    description: "Entendiendo los mensajes de error de Supabank",
    category: "troubleshooting",
    order: 4,
    keywords: ["error", "código", "mensaje", "fallido", "problema"],
    content: `
# Explicación de códigos de error

Entender los mensajes de error ayuda a resolver problemas más rápido.

## Códigos de error comunes

### Errores de autenticación

| Código | Significado | Solución |
|--------|-------------|----------|
| AUTH_001 | Sesión expirada | Inicia sesión de nuevo |
| AUTH_002 | Enlace mágico inválido | Solicita nuevo enlace |
| AUTH_003 | 2FA requerido | Introduce código 2FA |
| AUTH_004 | Demasiados intentos | Espera 30 minutos |

### Errores de transacción

| Código | Significado | Solución |
|--------|-------------|----------|
| TXN_001 | Saldo insuficiente | Verifica saldo |
| TXN_002 | Por debajo del mínimo | Aumenta el monto |
| TXN_003 | Por encima del límite | Reduce el monto o sube de nivel |
| TXN_004 | Dirección inválida | Verifica formato de dirección |
| TXN_005 | Red no soportada | Usa red soportada |

### Errores de cuenta bancaria

| Código | Significado | Solución |
|--------|-------------|----------|
| BANK_001 | Routing inválido | Verifica número de routing |
| BANK_002 | Cuenta inválida | Verifica número de cuenta |
| BANK_003 | Nombre no coincide | Usa cuenta a tu nombre |
| BANK_004 | Verificación fallida | Re-verifica la cuenta |

### Errores de verificación

| Código | Significado | Solución |
|--------|-------------|----------|
| KYC_001 | Documento no claro | Vuelve a tomar la foto |
| KYC_002 | Documento caducado | Usa ID válido |
| KYC_003 | Selfie no coincide | Vuelve a tomar selfie |
| KYC_004 | Región no soportada | Verifica disponibilidad |

## Formato de mensaje de error

\`\`\`
ERROR: [CÓDIGO]
Mensaje: Descripción legible
Detalles: Información adicional
Acción: Siguiente paso sugerido
\`\`\`

## Si el error persiste

1. Anota el código de error
2. Intenta la solución sugerida
3. Si sigue fallando, contacta con soporte
4. Incluye: código de error, captura de pantalla, pasos intentados
    `.trim(),
  },
  {
    id: "ts-005",
    slug: "common-issues-faq",
    title: "FAQ de problemas comunes",
    description: "Respuestas rápidas a problemas frecuentes",
    category: "troubleshooting",
    order: 5,
    keywords: ["faq", "común", "preguntas", "problemas", "frecuentes"],
    content: `
# FAQ de problemas comunes

Soluciones rápidas a los problemas más frecuentes.

## Problemas de inicio de sesión

**P: ¿El enlace mágico no llega?**
R: Revisa la carpeta de spam, verifica la ortografía del correo, espera 2 minutos y reintenta.

**P: ¿El enlace mágico caducó?**
R: Los enlaces caducan después de 15 minutos. Solicita uno nuevo.

**P: ¿No puedo acceder a mi dispositivo 2FA?**
R: Usa códigos de respaldo o contacta con soporte para restablecer 2FA.

## Problemas de transacciones

**P: ¿El depósito no aparece?**
R: Verifica la confirmación en blockchain. Contacta con soporte si confirmado hace >1 hora.

**P: ¿No recibí el pago?**
R: Verifica el estado en la app. ACH tarda 2-3 días hábiles. Contacta con soporte después de 5 días.

**P: ¿Se acreditó un monto incorrecto?**
R: Verifica la tasa de cambio en el momento de la conversión. Contacta con soporte con el ID de transacción.

## Problemas de verificación

**P: ¿El KYC sigue fallando?**
R: Usa buena iluminación, ID válido, fotos claras. Solicita revisión manual si persisten los problemas.

**P: ¿El nombre no coincide?**
R: Usa el nombre exactamente como aparece en tu ID. Contacta con soporte para cambio de nombre legal.

## Problemas de cuenta bancaria

**P: ¿Cuenta bancaria rechazada?**
R: Verifica todos los datos dos veces. La cuenta debe estar a tu nombre.

**P: ¿No recibí los micro-depósitos?**
R: Espera 2-3 días hábiles. Verifica que estés revisando la cuenta correcta.

## Problemas generales

**P: ¿La app no carga?**
R: Limpia la caché, prueba otro navegador, verifica la conexión a internet.

**P: ¿No puedo ver transacciones recientes?**
R: Desliza para actualizar o haz clic en el botón de actualizar. Verifica que los filtros no estén ocultando resultados.

**P: ¿Necesito actualizar mi correo?**
R: Ve a Ajustes > Seguridad > Cambiar correo. Verifica tanto el correo antiguo como el nuevo.

## ¿Aún necesitas ayuda?

Contacta con support@supabank.xyz con tu problema específico.
    `.trim(),
  },
  {
    id: "ts-006",
    slug: "contacting-support",
    title: "Contactar con soporte",
    description: "Cómo obtener ayuda de nuestro equipo de soporte",
    category: "troubleshooting",
    popular: true,
    order: 6,
    keywords: ["soporte", "ayuda", "contactar", "asistencia", "equipo"],
    content: `
# Contactar con soporte

Múltiples formas de obtener ayuda de nuestro equipo.

## Canales de soporte

### Asistente AI (Más rápido)
- Haz clic en la burbuja de chat en la app
- Disponible 24/7
- Respuestas instantáneas
- Puede crear tickets

### Soporte por correo
- support@supabank.xyz
- Respuesta: En 24 horas
- Mejor para problemas complejos
- Adjunta capturas de pantalla

### Centro de ayuda
- supabank.xyz/support
- Artículos de autoservicio
- Guías paso a paso
- Siempre disponible

## Qué incluir

### Para problemas de transacciones
- ID de transacción
- Monto y moneda
- Hash de blockchain (si es depósito)
- Capturas de pantalla

### Para problemas de verificación
- Correo de la cuenta
- Mensajes de error
- Fotos que intentas usar
- Información del dispositivo/navegador

### Para problemas bancarios
- Nombre del banco
- Mensaje de error
- (No envíes números de cuenta)

## Tiempos de respuesta

| Prioridad | Tiempo de respuesta |
|-----------|---------------------|
| Problemas de seguridad | 1 hora |
| Acceso a cuenta | 4 horas |
| Problemas de transacciones | 24 horas |
| Preguntas generales | 24-48 horas |

## Consejos para resolución más rápida

**Mejores prácticas:**
- Incluye todos los detalles relevantes desde el inicio
- Adjunta capturas de pantalla
- Usa un hilo por problema
- Revisa spam para respuestas
- Sé específico sobre el problema

## Escalamiento

Si el problema no se resuelve:
1. Responde al ticket existente
2. Solicita escalamiento
3. Incluye lo que has intentado
4. Permite 48 horas para revisión escalada

## Problemas de seguridad

Para asuntos de seguridad urgentes:
- Correo: security@supabank.xyz
- Incluye: Correo de cuenta, tipo de problema
- Respuesta: En 1 hora
    `.trim(),
  },
  {
    id: "ts-007",
    slug: "system-status",
    title: "Estado del sistema",
    description: "Verificar el estado de los servicios de Supabank",
    category: "troubleshooting",
    order: 7,
    keywords: ["estado", "caída", "fuera", "mantenimiento", "disponibilidad"],
    content: `
# Estado del sistema

Verifica si los servicios de Supabank están funcionando normalmente.

## Página de estado

Visita: **status.supabank.xyz**

Muestra estado en tiempo real para:
- Plataforma principal
- Servicios de API
- Procesamiento de pagos
- Monitoreo de blockchain

## Componentes del servicio

### Plataforma principal
- Aplicación web
- Autenticación de usuarios
- Funciones del panel

### Procesamiento de transacciones
- Detección de depósitos
- Motor de conversión
- Procesamiento de pagos

### Integraciones
- Nodos de blockchain
- Socios bancarios
- Proveedor de KYC

## Indicadores de estado

| Estado | Significado |
|--------|-------------|
| 🟢 Operacional | Todos los sistemas normales |
| 🟡 Degradado | Más lento de lo usual |
| 🔴 Caída | Servicio no disponible |
| 🔵 Mantenimiento | Tiempo de inactividad planificado |

## Suscribirse a actualizaciones

1. Visita status.supabank.xyz
2. Haz clic en "Suscribirse"
3. Introduce tu correo
4. Recibe notificaciones para:
   - Interrupciones del servicio
   - Ventanas de mantenimiento
   - Actualizaciones de resolución

## Durante caídas

### Qué hacer
- Revisa primero la página de estado
- No reintentes transacciones repetidamente
- Espera a la resolución
- Sigue a @supabank en Twitter para actualizaciones

### Qué NO hacer
Crear múltiples tickets de soporte
Reintentar transacciones fallidas continuamente
Asumir que los fondos están perdidos

## Mantenimiento programado

- Anunciado con 48 horas de anticipación
- Normalmente durante horas de bajo tráfico
- Típicamente breve (< 30 minutos)
- Actualizaciones publicadas en la página de estado

## Historial de disponibilidad

- Objetivo de 99.9% de disponibilidad
- Historial de incidentes en la página de estado
- Informes post-incidente publicados
    `.trim(),
  },
  {
    id: "ts-008",
    slug: "scheduled-maintenance",
    title: "Mantenimiento programado",
    description: "Entendiendo las ventanas de mantenimiento planificado",
    category: "troubleshooting",
    order: 8,
    keywords: ["mantenimiento", "programado", "inactividad", "planificado", "ventana"],
    content: `
# Mantenimiento programado

Cómo manejamos el mantenimiento planificado para minimizar interrupciones.

## Ventanas de mantenimiento

### Mantenimiento regular
- Cuándo: Domingos 2-4 AM UTC
- Duración: Normalmente < 30 minutos
- Impacto: Mínimo a ninguno

### Actualizaciones mayores
- Anunciadas con 48+ horas de anticipación
- Programadas durante bajo tráfico
- Puede haber breve interrupción del servicio

## Qué pasa durante el mantenimiento

### Típicamente afectado
- Nuevas transacciones pueden pausarse
- La UI puede mostrar página de mantenimiento
- La API puede devolver estado de mantenimiento

### Normalmente no afectado
- Transacciones pendientes continúan
- Monitoreo de blockchain
- Transferencias bancarias en progreso

## Cómo te notificamos

### 48+ horas antes
- Notificación por correo (si estás suscrito)
- Anuncio en página de estado
- Banner en la app

### Durante el mantenimiento
- Página de estado actualizada
- Pantalla de mantenimiento mostrada
- Tiempo estimado de finalización

### Después de completar
- Página de estado actualizada
- Servicio normal restaurado
- Resumen publicado (si es mayor)

## Qué debes hacer

### Antes del mantenimiento
Completa transacciones urgentes
No inicies operaciones grandes
Anota la duración esperada

### Durante el mantenimiento
Revisa la página de estado para actualizaciones
Espera pacientemente
No reintentes operaciones repetidamente

### Después del mantenimiento
Actualiza tu navegador
Verifica transacciones pendientes
Reporta cualquier problema

## Mantenimiento de emergencia

Ocasionalmente necesario para:
- Parches de seguridad
- Correcciones de bugs críticos
- Problemas de infraestructura

Estos tienen menos aviso previo pero siguen patrones de comunicación similares.

## ¿Preguntas?

Contacta con support@supabank.xyz para preguntas relacionadas con mantenimiento.
    `.trim(),
  },
];
